import React, { useState, useEffect, useMemo } from "react";
import { GetTeamTransferRequests } from "../Services/RecruitmentService";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import {
  ApproveTransfer,
  RejectTransfer,
} from "../Services/RecruitmentService";
import {
  User, RefreshCcw, Search, Mail, Phone, ChevronDown,
  History,
} from "lucide-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import RecruitmentHistoryDrawer from "../Modals/RecruitmentHistoryDrawer";
import { formatDateTimeU } from "../../../../Utils/FormUtils";

const TeamTransferRequests = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [requests, SetRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRequests, setTotalRequests] = useState(0);
  const [alert, setAlert] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [remark, setRemark] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [openHistoryDrawer, setOpenHistoryDrawer] = useState(false);
  const [selectedHistoryLead, setSelectedHistoryLead] = useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await GetTeamTransferRequests(page, pageSize);
      SetRequests(res?.leadTransferRequests || []);
      setTotalRequests(res?.totalRequests || 0);
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Failed to fetch lead transfer requests.",
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(page, pageSize);
  }, [page, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalRequests / pageSize);
  }, [totalRequests, pageSize]);

  const approveTransfer = async (requestUID) => {
    if (!remark.trim()) {
      setAlert({
        type: "error",
        message: "Remark is required before approving.",
      });
      return;
    }
    try {
      setActionLoading((prev) => ({
        ...prev,
        [requestUID]: "approve",
      }));

      const res = await ApproveTransfer(requestUID, remark);

      if (res?.status !== "success") {
        throw new Error(res?.message || "Failed to approve transfer.");
      }

      setAlert({
        type: "success",
        message: res.message,
      });

      setExpandedRequest(null);

      fetchRequests();
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Failed to approve transfer request.",
      });
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [requestUID]: null,
      }));
    }
  };

  const rejectTransfer = async (requestUID) => {
    if (!remark.trim()) {
      setAlert({
        type: "error",
        message: "Remark is required before rejecting.",
      });
      return;
    }
    try {
      setActionLoading((prev) => ({
        ...prev,
        [requestUID]: "reject",
      }));

      const res = await RejectTransfer(requestUID, remark);

      if (res?.status !== "success") {
        throw new Error(res?.message || "Failed to reject transfer.");
      }

      setAlert({
        type: "success",
        message: res.message,
      });

      setExpandedRequest(null);

      fetchRequests();
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Failed to reject transfer request.",
      });
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [requestUID]: null,
      }));
    }
  };

  const LeadsSkeleton = ({ rows = 6 }) => {
    return (
      <SkeletonTheme baseColor="#f1f5f9" highlightColor="#ffffff">
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className="
              grid
              grid-cols-[2.2fr_1.4fr_1.4fr_1fr_1.2fr_1.2fr]
              gap-6
              px-6 py-4
              border-b border-slate-100
              items-start
            "
          >
            {/* LEAD */}
            <div className="flex gap-3">
              <Skeleton circle width={40} height={40} />

              <div className="flex-1">
                <Skeleton height={16} width="65%" borderRadius={6} />

                <Skeleton
                  height={12}
                  width="85%"
                  borderRadius={6}
                  className="mt-2"
                />
              </div>
            </div>

            {/* REQUESTED BY */}
            <div>
              <Skeleton height={16} width="75%" borderRadius={6} />

              <Skeleton
                height={12}
                width="45%"
                borderRadius={6}
                className="mt-2"
              />
            </div>

            {/* OWNER */}
            <div>
              <Skeleton height={16} width="75%" borderRadius={6} />

              <Skeleton
                height={12}
                width="45%"
                borderRadius={6}
                className="mt-2"
              />
            </div>

            {/* STATUS */}
            <div>
              <Skeleton height={30} width={90} borderRadius={10} />
            </div>

            {/* REQUESTED */}
            <div>
              <Skeleton height={16} width="70%" borderRadius={6} />

              <Skeleton
                height={12}
                width="40%"
                borderRadius={6}
                className="mt-2"
              />
            </div>

            {/* UPDATED */}
            <div>
              <Skeleton height={16} width="70%" borderRadius={6} />

              <Skeleton
                height={12}
                width="40%"
                borderRadius={6}
                className="mt-2"
              />
            </div>
          </div>
        ))}
      </SkeletonTheme>
    );
  };

  return (
    <>
      <div className="p-2 space-y-3">
        {/*  TOOLBAR  */}

        <div className="max-w-8xl mx-auto bg-white border border-slate-200 rounded-xl shadow-sm px-5 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* LEFT */}

            <div className="flex items-center gap-4 flex-1">
              {/* SEARCH */}

              <div className="flex items-center w-full max-w-md bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search candidate..."
                  className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setPage(1);

                      setActiveSearch(search);
                    }
                  }}
                />

                <button
                  onClick={() => {
                    setPage(1);
                    setActiveSearch(search);
                  }}
                  className="h-10 w-10 border-l border-slate-200 flex items-center justify-center hover:bg-slate-100"
                >
                  <Search size={16} className="text-slate-500" />
                </button>

                {activeSearch && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setActiveSearch("");
                      setPage(1);
                    }}
                    className="h-10 w-10 border-l border-slate-200 flex items-center justify-center hover:bg-slate-100"
                  >
                    <RefreshCcw size={15} className="text-red-500" />
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-5">
              <div className="text-sm text-slate-600">
                Showing{" "}
                <span className="font-semibold">
                  {totalRequests === 0
                    ? "0"
                    : `${(page - 1) * pageSize + 1}-${Math.min(
                        page * pageSize,
                        totalRequests,
                      )}`
                  }
                </span>{" "}
                of <span className="font-semibold">{totalRequests}</span>
              </div>

              {/* ROWS */}

              <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
                <span className="text-sm text-slate-500">Rows</span>

                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                  className="h-8 px-2 rounded-lg border border-slate-200 bg-slate-50 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ================= TABLE ================= */}

        <div className="max-w-8xl mx-auto bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {/* HEADER */}

          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            <div className="col-span-2 pl-12">Lead</div>
            <div className="col-span-2">Requested By</div>
            <div className="col-span-2">Owner</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 pl-1">Requested</div>
            <div className="col-span-2 pl-1">Last Updated</div>
            <div className="col-span-1 pl-1">History</div>
          </div>

          {/* BODY */}

          <div className="divide-y divide-slate-100">
            {loading && initialLoading ? (
              <div className="bg-white">
                <LeadsSkeleton rows={5} />
              </div>
            ) : requests.length === 0 ? (
              <div className="py-16 flex flex-col items-center">
                <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                  <User size={22} className="text-slate-400" />
                </div>

                <h3 className="text-sm font-semibold text-slate-700">
                  No transfer requests found
                </h3>
              </div>
            ) : (
              requests.map((item, index) => {
                const statusClass =
                  item.status === 1
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : item.status === 2
                      ? "bg-red-50 text-red-700 border-red-100"
                      : "bg-amber-50 text-amber-700 border-amber-100";

                const requested = formatDateTimeU(item.requestedAt, { split: true });
                const updated = formatDateTimeU(item.lastUpdatedDate, { split: true });

                const expanded = expandedRequest === item.requestUID;

                return (
                  <div
                    key={item.requestUID}
                    className="border-b border-slate-100 last:border-0"
                  >
                    {/* SUMMARY ROW */}

                    <div className="grid grid-cols-12 gap-4 px-6 py-4 items-start cursor-pointer hover:bg-blue-50/40 transition-all duration-200">
                      {/* Lead */}
                      <div
                        onClick={() => {
                          setRemark("")
                          setExpandedRequest(expanded ? null : item.requestUID)
                        }}
                        className="col-span-2 flex gap-3 items-start group"
                      >
                        <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center font-semibold text-blue-700 shrink-0">
                          {item.leadName?.charAt(0)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm truncate transition-colors text-blue-600">
                              {item.leadName}
                            </p>

                            <ChevronDown
                              size={15}
                              className={`transition-all duration-200 text-blue-600 ${expanded ? "rotate-180" : ""}`}
                            />
                          </div>

                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            {item.email ? (
                              <>
                                <Mail size={12} />
                                <span className="truncate">{item.email}</span>
                              </>
                            ) : (
                              <>
                                <Phone size={12} />
                                <span>{item.phoneNo}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* REQUESTED BY */}
                      <div className="col-span-2">
                        <p className="text-sm font-medium">
                          {item.requestedByName}
                        </p>

                        <p className="text-xs text-slate-500">
                          {item.requestedByCode}
                        </p>
                      </div>

                      {/* OWNER */}
                      <div className="col-span-2">
                        <p className="text-sm font-medium">
                          {item.currentOwnerName}
                        </p>

                        <p className="text-xs text-slate-500">
                          {item.currentOwnerCode}
                        </p>
                      </div>

                      {/* STATUS */}
                      <div className="col-span-1">
                        <span
                          className={`inline-flex px-2 py-1 rounded-lg border text-xs font-medium ${statusClass}`}
                        >
                          {item.statusText}
                        </span>
                      </div>

                      {/* REQUESTED */}
                      <div className="col-span-2">
                        <div className="flex flex-col items-start">
                          <p className="text-sm">{requested.date}</p>

                          <p className="text-xs text-slate-400">
                            {requested.time}
                          </p>
                        </div>
                      </div>

                      {/* UPDATED */}
                      <div className="col-span-2">
                        <div className="flex flex-col items-start">
                          <p className="text-sm">{updated.date}</p>

                          <p className="text-xs text-slate-400">
                            {updated.time}
                          </p>
                        </div>
                      </div>
                      {/* View History */}
                      <div className="col-span-1 flex justify-center mr-6">
                        <button
                          onClick={() => {
                            setSelectedHistoryLead(item);
                            setOpenHistoryDrawer(true);
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium shadow-sm hover:opacity-90 transition-all duration-200 whitespace-nowrap"
                        >
                          <History size={14} />
                          <span>View History</span>
                        </button>
                      </div>
                    </div>

                    {/* EXPANDABLE PANEL */}
                    <div
                      className={`mx-4 overflow-hidden rounded-xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] transform-gpu 
                                 ${
                                    expanded
                                      ? `mb-4 border border-[#d9e6f2] bg-gradient-to-br from-[#edf4fb] via-[#f8fbff] to-white shadow-[0_8px_30px_rgba(148,163,184,0.10)] max-h-[540px] opacity-100 translate-y-0`
                                      : `mb-0 border border-transparent bg-transparent shadow-none max-h-0 opacity-0 -translate-y-2 pointer-events-none`
                                  }
                                `}     
                    >
                      {/* HEADER SNAPSHOT */}
                      <div className="p-4">
                        {/* TOP CARDS */}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                          {[
                            {
                              label: "Experience",
                              value: item.totalExperience
                                ? `${item.totalExperience} yrs`
                                : "-",
                            },
                            {
                              label: "Current Location",
                              value: item.currentJobLocation || "-",
                            },
                            {
                              label: "Preferred Location",
                              value: item.preferredJobLocation || "-",
                            },
                            {
                              label: "Company",
                              value: item.currentCompany || "-",
                            },
                          ].map((card, idx) => (
                            <div
                              key={idx}
                              className="rounded-2xl border border-slate-200/80 bg-white px-3 py-2"
                            >
                              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
                                {card.label}
                              </p>

                              <p className="text-sm font-semibold text-slate-800 mt-2">
                                {card.value}
                              </p>
                            </div>
                          ))}
                        </div>

                        {/* DETAILS TABLE */}

                        <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden">
                          <div className="grid md:grid-cols-3 border-b border-slate-200">
                            <div className="px-3 py-2 border-r border-slate-200">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                Interested In
                              </p>

                              <p className="text-sm font-semibold text-slate-700 mt-2">
                                {item.appliedFor || "-"}
                              </p>
                            </div>

                            <div className="px-3 py-2 border-r border-slate-200">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                Designation
                              </p>

                              <p className="text-sm font-semibold text-slate-700 mt-2">
                                {item.designation || "-"}
                              </p>
                            </div>

                            <div className="px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                Skills
                              </p>

                              <div className="flex flex-wrap gap-2 mt-2">
                                {(item.skills || "")
                                  .split(",")
                                  .filter(Boolean)
                                  .map((skill, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-[11px] font-medium text-slate-700"
                                    >
                                      {skill.trim()}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          </div>

                          <div className="grid md:grid-cols-3">
                            <div className="px-3 py-2 border-r border-slate-200">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                Requested By
                              </p>

                              <p className="text-sm font-semibold text-slate-700 mt-2">
                                {item.requestedByName}
                              </p>

                              <p className="text-xs text-slate-500 mt-1">
                                {item.requestedByCode}
                              </p>
                            </div>

                            <div className="px-3 py-2 border-r border-slate-200">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                Current Owner
                              </p>

                              <p className="text-sm font-semibold text-slate-700 mt-2">
                                {item.currentOwnerName}
                              </p>

                              <p className="text-xs text-slate-500 mt-1">
                                {item.currentOwnerCode}
                              </p>
                            </div>

                            <div className="px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                Candidate Location
                              </p>

                              <p className="text-sm font-semibold text-slate-700 mt-2">
                                {item.city}, {item.state}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* TRANSFER FLOW */}

                        <div className="mt-5">
                          <div className="inline-flex items-center rounded-2xl border border-blue-100 bg-blue-50/60 px-3 py-2">
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                                From
                              </p>

                              <p className="text-sm font-semibold text-slate-800 mt-1">
                                {item.currentOwnerName}
                              </p>

                              <p className="text-xs text-slate-500 mt-1">
                                {item.currentOwnerCode}
                              </p>
                            </div>

                            <div className="h-10 w-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-400 mx-4">
                              →
                            </div>

                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                                To
                              </p>

                              <p className="text-sm font-semibold text-slate-800 mt-1">
                                {item.requestedByName}
                              </p>

                              <p className="text-xs text-slate-500 mt-1">
                                {item.requestedByCode}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* ACTION BAR */}

                        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 px-3 py-2 rounded-2xl border border-slate-200 bg-white">
                              <span className="text-sm font-medium text-slate-500 border-r border-slate-200 pr-3">
                                Remark <span className="text-red-500">*</span>
                              </span>

                              <input
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                                placeholder="Add remark for approval or rejection"
                                className="flex-1 bg-transparent outline-none text-sm"
                              />
                            </div>
                          </div>

                          <button
                            disabled={actionLoading === "approve"}
                            onClick={(e) => {
                              e.stopPropagation();
                              approveTransfer(item.requestUID);
                            }}
                            className="h-10 px-6 rounded-2xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                          >
                            Transfer
                          </button>

                          <button
                            disabled={actionLoading === "reject"}
                            onClick={(e) => {
                              e.stopPropagation();
                              rejectTransfer(item.requestUID);
                            }}
                            className="h-10 px-6 rounded-2xl border border-red-200 bg-white text-red-600 text-sm font-semibold hover:bg-red-50"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ================= PAGINATION ================= */}
          {!initialLoading && totalPages >= 1 && (
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                              ${
                                page === 1
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                            `}
                >
                  Prev
                </button>

                {(() => {
                  const pages = [];

                  for (let i = 1; i <= totalPages; i++) {
                    if (
                      i === 1 ||
                      i === totalPages ||
                      (i >= page - 1 && i <= page + 1)
                    ) {
                      pages.push(i);
                    }
                  }

                  return pages.map((pageNumber, index) => {
                    const prevPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {prevPage && pageNumber - prevPage > 1 && (
                          <span className="px-1 text-sm text-slate-600">
                            ...
                          </span>
                        )}

                        <button
                          onClick={() => setPage(pageNumber)}
                          className={`h-8 min-w-[34px] px-2 rounded-lg text-sm font-semibold transition-all
                                      ${
                                        page === pageNumber
                                          ? "bg-primary text-white shadow-sm"
                                          : "text-slate-600 hover:bg-slate-100"
                                      }
                                    `}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((prev) => prev + 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                              ${
                                page === totalPages
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                            `}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}

      <RecruitmentHistoryDrawer
        open={openHistoryDrawer}
        onClose={() => {
          setOpenHistoryDrawer(false);
          setSelectedHistoryLead(null);
        }}
        recruitmentUID={selectedHistoryLead?.leadUID}
      />
    </>
  );
};

export default TeamTransferRequests;
